<script setup>
  import { ref, computed, inject, onMounted, onBeforeUnmount } from "vue";
  import { storeToRefs } from "pinia";
  import { Copy, Plus, Trash2, X } from "lucide-vue-next";

  import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
  import SearchInput from "@/components/episto-ui/search-input/SearchInput.vue";
  import { Button } from "@/components/ui/button";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";

  import CardsList from "@/components/Survey/Components/CardsList/CardsList.vue";
  import CardTypePickerDialog from "@/components/Survey/Edit/CardTypePickerDialog.vue";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useSurveySectionsStore } from "@stores/survey/surveySections";

  const props = defineProps({
    cardSubTypesInfos: { type: Object, required: true },
    cards: { type: Array, required: true },
    errorsByCard: { type: Object, default: () => ({}) },
    eventBusName: { type: String, required: true },
    isCardFilteredOut: { type: Function, required: true },
    isFilterActive: { type: Boolean, required: true },
    isFooterHidden: { type: Boolean, default: false },
    isHeaderHidden: { type: Boolean, default: false },
    isListReadOnly: { type: Boolean, default: false },
    isListWide: { type: Boolean, default: false },
    questionCount: { type: Number, required: true },
    selectedCard: { type: Object, default: () => ({}) },
    selectedCardToken: { type: String, default: "" },
    onlyQuestion: { type: Boolean, default: false },
    isTestMode: { type: Boolean, default: false },
  });

  const cardList = ref(null);

  const sectionsStore = useSurveySectionsStore();
  const { selectedCardTokens, numberOfSelectedCards } = storeToRefs(sectionsStore);
  const { resetSelectedCards } = sectionsStore;
  const isSelecting = computed(() => numberOfSelectedCards.value > 0);

  const eventBus = inject(props.eventBusName);
  const addCardDialogOpen = ref(false);

  const selectedInOrder = () =>
    props.cards.filter(({ token }) => selectedCardTokens.value.includes(token)).map(({ token }) => token);

  const duplicateSelection = () => {
    eventBus.emit({ command: "duplicateMany", tokens: selectedInOrder() });
    resetSelectedCards();
  };

  const {
    isOpen: isDeleteSelectionOpen,
    requestDelete: requestSelectionDelete,
    confirm: confirmSelectionDelete,
  } = useConfirmDelete((tokens) => {
    tokens.forEach((token) => eventBus.emit({ command: "remove", token }));
    resetSelectedCards();
  });

  const onEscape = (event) => {
    if (event.key === "Escape" && isSelecting.value) resetSelectedCards();
  };
  onMounted(() => document.addEventListener("keydown", onEscape));
  onBeforeUnmount(() => document.removeEventListener("keydown", onEscape));

  const handleAddCardAtTheEnd = (subType) => {
    eventBus.emit({ command: "addAtTheEnd", type: subType });
    cardList.value?.cardScrollTopScrollbar();
  };

  // Direct store binding for search
  const surveyCardsStore = useSurveyCardsStore();

  // Use store's searchQuery directly with computed getter/setter for v-model
  const localSearchQuery = computed({
    get: () => surveyCardsStore.searchQuery,
    set: (value) => {
      surveyCardsStore.searchQuery = value;
    },
  });

  defineExpose({
    cardList,
  });
</script>

<template>
  <Card
    :class="{
      'pt-0': isHeaderHidden && isFooterHidden,
      'pb-0': isFooterHidden,
    }"
    class="flex flex-col !gap-0 !py-0 h-fit max-h-[calc(100vh-100px)] overflow-hidden shadow-xs"
    data-cy="cards-pane">
    <CardHeader
      v-if="!isHeaderHidden"
      class="flex flex-shrink-0 flex-col items-stretch gap-1.5 px-3 pb-2 pt-3">
      <div
        v-if="isSelecting && !isListReadOnly"
        class="flex h-8 items-center gap-1 rounded-lg bg-secondary pl-3 pr-1 text-13">
        <span class="shrink-0 font-medium text-primary">{{ numberOfSelectedCards }} selected</span>
        <Button
          variant="ghost"
          size="sm"
          class="ml-auto text-muted-foreground hover:text-primary"
          @click="duplicateSelection">
          <Copy class="size-3.5" />
          Duplicate
        </Button>
        <Button
          variant="ghost"
          size="sm"
          class="text-muted-foreground hover:text-status-error-strong"
          @click="requestSelectionDelete(selectedInOrder())">
          <Trash2 class="size-3.5" />
          Delete
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Leave selection"
          class="text-muted-foreground hover:text-primary"
          @click="resetSelectedCards">
          <X class="size-3.5" />
        </Button>
      </div>
      <template v-else>
        <SearchInput
          v-model="localSearchQuery"
          variant="muted"
          placeholder="Search cards…"
          class="min-w-0"
          data-cy="cards-searchbar" />
        <p class="px-1 text-13 text-muted-foreground">{{ questionCount }} questions</p>
      </template>
    </CardHeader>
    <CardContent class="flex-1 flex flex-col min-h-0 p-1">
      <CardsList
        ref="cardList"
        :card-sub-types-infos="cardSubTypesInfos"
        :cards="cards"
        :errors-by-card="errorsByCard"
        :event-bus-name="eventBusName"
        :is-card-filtered-out="isCardFilteredOut"
        :is-filter-active="isFilterActive"
        :is-list-read-only="isListReadOnly"
        :selected-card="selectedCard"
        :selected-card-token="selectedCardToken"
        :only-question="onlyQuestion"
        :is-footer-hidden="isFooterHidden"
        :is-test-mode="isTestMode"
        :question-count="questionCount" />
    </CardContent>
    <CardFooter
      v-if="!isListReadOnly && !isFooterHidden"
      id="nav-pane-footer"
      class="shrink-0 border-t bg-background px-3 py-2.5">
      <Button
        variant="outline"
        :disabled="isFilterActive"
        class="w-full shadow-xs"
        @click="addCardDialogOpen = true">
        <Plus />
        Add card
      </Button>
      <CardTypePickerDialog
        v-model:open="addCardDialogOpen"
        title="Add card"
        description="Select a card type to add"
        @select="handleAddCardAtTheEnd" />
    </CardFooter>
    <ConfirmDialog
      v-model:open="isDeleteSelectionOpen"
      title="Delete selected cards"
      description="Are you sure you want to delete these cards? This change cannot be undone."
      confirm-label="Yes, delete"
      cancel-label="No, cancel"
      @confirm="confirmSelectionDelete" />
  </Card>
</template>
